// Fixed-timestep game loop: simulation always steps at exactly 1/60s, so
// v1's `value * deltaTime * 60` physics formulas evaluate with dt*60 === 1
// and unit tests are deterministic. Rendering runs every animation frame.
// timeScale supports combat time dilation (v1 slowed to 0.1).

import { FIXED_STEP, MAX_FRAME_DELTA } from '@/config/tuning';

export interface LoopCallbacks {
  update: (dt: number) => void;
  render: (alpha: number) => void;
}

export class GameLoop {
  timeScale = 1.0;
  private accumulator = 0;
  private lastTime = 0;
  private rafId = 0;
  private running = false;

  constructor(private readonly callbacks: LoopCallbacks) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.accumulator = 0;
    const frame = (now: number): void => {
      if (!this.running) return;
      // Clamp like v1 (js/game3d.js:497) so tab-switch gaps don't spiral
      const delta = Math.min((now - this.lastTime) / 1000, MAX_FRAME_DELTA);
      this.lastTime = now;
      this.accumulator += delta * this.timeScale;

      let steps = 0;
      while (this.accumulator >= FIXED_STEP && steps < 3) {
        this.callbacks.update(FIXED_STEP);
        this.accumulator -= FIXED_STEP;
        steps++;
      }
      // Drop excess backlog beyond the step cap instead of accumulating debt
      if (this.accumulator >= FIXED_STEP) this.accumulator = this.accumulator % FIXED_STEP;

      this.callbacks.render(this.accumulator / FIXED_STEP);
      this.rafId = requestAnimationFrame(frame);
    };
    this.rafId = requestAnimationFrame(frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }
}
